<template>
  <div>
    <div class="row">
      <div class="col-12">
        <div class="card">
          <div class="card-header d-flex justify-content-between align-items-center">
            <div class="d-flex align-items-center">
              <button 
                class="btn btn-secondary btn-sm mr-3" 
                @click="goBack"
                title="Voltar"
              >
                <i class="fas fa-arrow-left"></i>
              </button>
              <h4 class="mb-0">{{ $t('reservations.reservation') }}: {{ reservationName }}</h4>
            </div>
            <div class="reservation-status" v-if="reservation">
              <span class="badge" :class="`badge-${getStatusClass(reservation.status)}`">
                {{ getStatusText(reservation.status) }}
              </span>
            </div>
          </div>
          <div class="card-body">
            <div v-if="loading" class="text-center">
              <div class="spinner-border" role="status">
                <span class="sr-only">{{ $t('loading') }}</span>
              </div>
            </div>
            
            <div v-else-if="reservation && reservation.id" class="reservation-details">
              <!-- Reservation Overview -->
              <div class="row mb-4">
                <div class="col-md-6">
                  <h5>{{ $t('reservations.experienceDetails') }}</h5>
                  <table class="table table-sm">
                        <tr>
                          <td><strong>{{ $t('reservations.experience') }}:</strong></td>
                          <td>{{ displayedReservation.experienceName || 'N/A' }}</td>
                        </tr>
                        <tr>
                          <td><strong>{{ $t('reservations.date') }}:</strong></td>
                          <td>{{ displayedReservation.date ? formatDate(displayedReservation.date) : 'N/A' }}</td>
                        </tr>
                        <tr>
                          <td><strong>{{ $t('reservations.time') }}:</strong></td>
                          <td>{{ displayedReservation.time || 'N/A' }}</td>
                        </tr>
                        <tr>
                          <td><strong>{{ $t('reservations.people') }}:</strong></td>
                          <td>{{ displayedReservation.numberOfPeople || 0 }}</td>
                        </tr>
                        <tr>
                          <td><strong>{{ $t('reservations.language') }}:</strong></td>
                          <td>{{ reservation ? getLanguageName(reservation.language) : 'N/A' }}</td>
                        </tr>
                  </table>
                </div>
                <div class="col-md-6">
                  <h5>{{ $t('reservations.contactInfo') }}</h5>
                  <table class="table table-sm">
                        <tr>
                          <td><strong>{{ $t('reservations.tourist') }}:</strong></td>
                          <td>{{ reservation ? reservation.touristName : 'N/A' }}</td>
                        </tr>
                        <tr>
                          <td><strong>{{ $t('reservations.email') }}:</strong></td>
                          <td>{{ reservation ? reservation.touristEmail : 'N/A' }}</td>
                        </tr>
                        <tr>
                          <td><strong>{{ $t('reservations.phone') }}:</strong></td>
                          <td>{{ reservation ? reservation.touristPhone : 'N/A' }}</td>
                        </tr>
                        <tr v-if="reservation && reservation.prePaymentRequired">
                          <td><strong>{{ $t('reservations.payment') }}:</strong></td>
                          <td>{{ reservation ? getPaymentMethodText(reservation.paymentMethod) : 'N/A' }}</td>
                        </tr>
                        <tr v-if="reservation && reservation.basePrice != null">
                          <td><strong>{{ $t('reservations.basePrice') }}:</strong></td>
                          <td>€{{ formatPrice(reservation.basePrice) }}</td>
                        </tr>
                        <tr v-if="reservation && reservation.totalPrice != null">
                          <td><strong>{{ $t('reservations.totalPrice') }}:</strong></td>
                          <td>€{{ formatPrice(reservation.totalPrice) }}</td>
                        </tr>
                        <tr v-if="reservation && reservation.prePaymentRequired && reservation.prePaymentAmount > 0">
                          <td><strong>{{ $t('reservations.prePaymentAmount') }}:</strong></td>
                          <td>€{{ formatPrice(reservation.prePaymentAmount) }}</td>
                        </tr>
                        <tr v-if="reservation && reservation.voucherCode">
                          <td><strong>{{ $t('reservations.voucherCode') }}:</strong></td>
                          <td>{{ reservation.voucherCode }}</td>
                        </tr>
                        <tr>
                          <td><strong>{{ $t('reservations.status') }}:</strong></td>
                          <td>
                            <span v-if="reservation" class="badge" :class="`badge-${getStatusClass(reservation.status)}`">
                              {{ getStatusText(reservation.status) }}
                            </span>
                            <span v-else>N/A</span>
                          </td>
                        </tr>
                  </table>
                </div>
              </div>

              <!-- People Details -->
              <div class="row mb-4">
                <div class="col-12">
                  <h5>{{ $t('reservations.peopleDetails') }}</h5>
                  <div class="table-responsive">
                    <table class="table table-striped">
                      <thead>
                        <tr>
                          <th>{{ $t('reservations.personName') }}</th>
                          <th>{{ $t('reservations.ageBracket') }}</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="(person, index) in peopleDetails" :key="index">
                          <td>{{ person.name }}</td>
                          <td>{{ getAgeBracketName(person.ageBracket) }}</td>
                        </tr>
                        <tr v-if="peopleDetails.length === 0">
                          <td colspan="2" class="text-center text-muted">{{ $t('reservations.noPeopleDetails') }}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>

              <!-- Additional Information -->
              <div class="row mb-4">
                <div class="col-md-6">
                  <h5>{{ $t('reservations.restrictions') }}</h5>
                  <p v-if="reservation && reservation.restrictions" class="text-muted">{{ reservation.restrictions }}</p>
                  <p v-else class="text-muted">{{ $t('reservations.noRestrictions') }}</p>
                </div>
                <div class="col-md-6">
                  <h5>{{ $t('reservations.notes') }}</h5>
                  <p v-if="reservation && reservation.notes" class="text-muted">{{ reservation.notes }}</p>
                  <p v-else class="text-muted">{{ $t('reservations.noNotes') }}</p>
                </div>
              </div>

              <!-- Admin Notes -->
              <div class="row mb-4">
                <div class="col-12">
                  <h5>{{ $t('reservations.adminNotes') }}</h5>
                  <textarea
                    class="form-control"
                    rows="3"
                    v-model="reservation.adminNotes"
                    :placeholder="$t('reservations.adminNotesPlaceholder')"
                    @blur="updateAdminNotes"
                    v-if="reservation"
                  ></textarea>
                  <p v-else class="text-muted">Loading...</p>
                </div>
              </div>

              <!-- Client Note -->
              <div class="row mb-4">
                <div class="col-12">
                  <h5>{{ $t('reservations.clientNote') }}</h5>
                  <textarea
                    class="form-control"
                    rows="3"
                    v-model="clientNote"
                    :placeholder="$t('reservations.clientNotePlaceholder')"
                    v-if="reservation"
                  ></textarea>
                  <p v-else class="text-muted">Loading...</p>
                  <small v-if="reservation && reservation.clientNoteLastUpdatedAt" class="text-muted">
                    {{ $t('reservations.clientNoteLastUpdated') }}: {{ formatDateTime(reservation.clientNoteLastUpdatedAt) }}
                  </small>
                  <div class="mt-2" v-if="reservation">
                    <button
                      class="btn btn-secondary mr-2"
                      @click="saveClientNote(false)"
                      :disabled="clientNoteSaving"
                    >
                      <i class="fas fa-save"></i> {{ $t('reservations.saveClientNote') }}
                    </button>
                    <button
                      class="btn btn-primary"
                      @click="showClientNoteModal = true"
                      :disabled="clientNoteSaving || !clientNote"
                    >
                      <i class="fas fa-envelope"></i> {{ $t('reservations.saveAndSendClientNote') }}
                    </button>
                  </div>
                </div>
              </div>

              <!-- Actions -->
              <div class="row">
                <div class="col-12">
                  <div class="d-flex justify-content-between">
                    <div>
                      <button 
                        class="btn btn-success mr-2" 
                        @click="showConfirmModal = true"
                        :disabled="!reservation || reservation.status === 'Confirmed' || reservation.status === 'Cancelled'"
                      >
                        <i class="fas fa-check"></i> {{ $t('reservations.confirm') }}
                      </button>
                      <button 
                        class="btn btn-danger mr-2" 
                        @click="showCancelModal = true"
                        :disabled="!reservation || reservation.status === 'Cancelled'"
                      >
                        <i class="fas fa-times"></i> {{ $t('reservations.cancel') }}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    
    <!-- Confirm Reservation Modal -->
    <div v-if="showConfirmModal" class="modal-overlay" @click="cancelConfirmation">
      <div class="modal-content" @click.stop>
        <div class="modal-header">
          <h5 class="modal-title">{{ $t('reservations.confirm') }}</h5>
          <button type="button" class="close" @click="cancelConfirmation">
            <span>&times;</span>
          </button>
        </div>
        <div class="modal-body">
          <p>{{ $t('reservations.confirmReservation') }}</p>
          <p v-if="reservation && displayedReservation" class="text-muted">
            <strong>{{ $t('reservations.tourist') }}:</strong> {{ reservation.touristName || 'N/A' }}<br>
            <strong>{{ $t('reservations.experience') }}:</strong> {{ displayedReservation.experienceName || 'N/A' }}<br>
            <strong>{{ $t('reservations.date') }}:</strong> {{ displayedReservation.date ? formatDate(displayedReservation.date) : 'N/A' }}
          </p>
        </div>
        <div class="modal-footer">
          <button type="button" class="btn btn-secondary" @click="cancelConfirmation">
            {{ $t('cancel') }}
          </button>
          <button type="button" class="btn btn-success" @click="confirmReservationHandler">
            Sim
          </button>
        </div>
      </div>
    </div>
    
    <!-- Cancel Reservation Modal -->
    <div v-if="showCancelModal" class="modal-overlay" @click="cancelCancelModal">
      <div class="modal-content" @click.stop>
        <div class="modal-header">
          <h5 class="modal-title">{{ $t('reservations.cancel') }}</h5>
          <button type="button" class="close" @click="cancelCancelModal">
            <span>&times;</span>
          </button>
        </div>
        <div class="modal-body">
          <p>{{ $t('reservations.confirmCancelReservation') }}</p>
          <p v-if="reservation && displayedReservation" class="text-muted">
            <strong>{{ $t('reservations.tourist') }}:</strong> {{ reservation.touristName || 'N/A' }}<br>
            <strong>{{ $t('reservations.experience') }}:</strong> {{ displayedReservation.experienceName || 'N/A' }}<br>
            <strong>{{ $t('reservations.date') }}:</strong> {{ displayedReservation.date ? formatDate(displayedReservation.date) : 'N/A' }}
          </p>
        </div>
        <div class="modal-footer">
          <button type="button" class="btn btn-secondary" @click="cancelCancelModal">
            {{ $t('cancel') }}
          </button>
          <button type="button" class="btn btn-danger" @click="cancelReservationHandler">
            Sim
          </button>
        </div>
      </div>
    </div>

    <!-- Client Note Confirmation Modal -->
    <div v-if="showClientNoteModal" class="modal-overlay" @click="showClientNoteModal = false">
      <div class="modal-content" @click.stop>
        <div class="modal-header">
          <h5 class="modal-title">{{ $t('reservations.confirmSendClientNote') }}</h5>
          <button type="button" class="close" @click="showClientNoteModal = false">
            <span>&times;</span>
          </button>
        </div>
        <div class="modal-body">
          <p>{{ $t('reservations.confirmSendClientNoteMessage') }}</p>
          <p v-if="reservation" class="text-muted">
            <strong>{{ $t('reservations.email') }}:</strong> {{ reservation.touristEmail || 'N/A' }}
          </p>
          <div class="client-note-preview">
            <strong>{{ $t('reservations.clientNote') }}:</strong>
            <p class="mt-2 p-2 bg-light border rounded">{{ clientNote }}</p>
          </div>
        </div>
        <div class="modal-footer">
          <button type="button" class="btn btn-secondary" @click="showClientNoteModal = false">
            {{ $t('cancel') }}
          </button>
          <button type="button" class="btn btn-primary" @click="saveClientNote(true)" :disabled="clientNoteSaving">
            <i class="fas fa-envelope"></i> {{ $t('sendEmail') }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState, mapActions } from 'vuex';

export default {
  computed: {
    reservationId() {
      return this.$route.query.id;
    },
    reservationName() {
      return this.$route.params.name || '';
    },
    passedTime() {
      return this.$route.params.time || '';
    },
    passedTotalPeople() {
      return this.$route.params.totalPeople || 0;
    },
    ...mapState('reservations', ['getReservationByIdOutput']),
    ...mapState('experiences', ['getExperiencesOutput']),
    reservation() {
      return this.getReservationByIdOutput?.data || null;
    },
    experienceIdToName() {
      const list = (this.getExperiencesOutput && this.getExperiencesOutput.data) ? this.getExperiencesOutput.data : [];
      const map = new Map();
      list.forEach(e => {
        if (e && e.id != null) {
          map.set(e.id, e.name || '');
        }
      });
      return map;
    },
    displayedReservation() {
      if (!this.reservation || !this.reservation.id) return {};
      
      // Use the same logic as ReservationsList.vue - prioritize availableDate
      const availability = this.reservation.experienceAvailability || null;
      const dateStr = (availability && availability.availableDate) || this.reservation.reservationDate || null;
      const start = availability && availability.startTime ? availability.startTime.toString().substring(0,5) : '';
      
      // Get experience name
      let experienceName = (this.reservation.experience && this.reservation.experience.name) ? this.reservation.experience.name : '';
      if (!experienceName && this.reservation.experienceId) {
        experienceName = this.experienceIdToName.get(this.reservation.experienceId) || `#${this.reservation.experienceId}`;
      }
      
      // Count people from multiple possible sources (same as ReservationsList)
      let totalPeople = 0;
      if (this.reservation.totalPeople && typeof this.reservation.totalPeople === 'number') {
        totalPeople = this.reservation.totalPeople;
      } else if (this.reservation.numberOfPeople && typeof this.reservation.numberOfPeople === 'number') {
        totalPeople = this.reservation.numberOfPeople;
      } else if (Array.isArray(this.reservation.people)) {
        totalPeople = this.reservation.people.length;
      } else if (this.reservation.reservationPeople && Array.isArray(this.reservation.reservationPeople)) {
        totalPeople = this.reservation.reservationPeople.length;
      } else if (this.reservation.people && typeof this.reservation.people === 'number') {
        totalPeople = this.reservation.people;
      }
      
      // Use passed parameters if available, otherwise use calculated values
      const timeValue = this.passedTime || (availability ? start : '');
      const peopleValue = this.passedTotalPeople || totalPeople;
      
      return {
        ...this.reservation,
        experienceName,
        date: dateStr,
        time: timeValue,
        numberOfPeople: peopleValue
      };
    },
    
    peopleDetails() {
      if (!this.reservation || !this.reservation.id) {
        return [];
      }
      
      // People data comes directly from the API in reservation.people
      if (Array.isArray(this.reservation.people) && this.reservation.people.length > 0) {
        return this.reservation.people.map(person => ({
          name: person.name || 'N/A',
          ageBracket: person.ageBracket // Pass raw enum value, template will translate
        }));
      }
      
      return [];
    }
  },
  data() {
    return {
      loading: false,
      showConfirmModal: false,
      showCancelModal: false,
      showClientNoteModal: false,
      clientNote: '',
      clientNoteSaving: false
    }
  },
  async mounted() {
    if (this.reservationId) {
      await this.loadReservation();
    }
  },
  methods: {
    ...mapActions('reservations', ['getById', 'update', 'confirmReservation', 'cancelReservation', 'updateClientNote']),
    ...mapActions('experiences', ['getExperiences']),
    
    async loadReservation() {
      try {
        this.loading = true;
        await Promise.all([
          this.getById(this.reservationId),
          this.getExperiences({ pageSize: 100, pageNumber: 1, propertyName: 'name' })
        ]);


        // Load additional data that getById doesn't include
        await this.loadAdditionalData();

        // Initialize client note from loaded reservation
        if (this.reservation) {
          this.clientNote = this.reservation.clientNote || '';
        }
      } catch (error) {
        console.error('Error loading reservation:', error);
        this.$store.dispatch('alert/setAlert', {
          type: 'error',
          message: this.$t('reservations.loadError')
        });
      } finally {
        this.loading = false;
      }
    },
    
    async loadAdditionalData() {
      if (!this.reservation || !this.reservation.id) return;
      
      try {
        const { API_HELPER } = await import('@/helpers/api.js');
        
        // Load availability data
        if (this.reservation.experienceId && this.reservation.reservationDate) {
          const availabilityUrl = API_HELPER.pathJoin([process.env.VUE_APP_API_ROOT, 'ExperienceAvailabilities']);
          const availabilityParams = {
            experienceId: this.reservation.experienceId,
            availableDate: this.reservation.reservationDate
          };
          
          try {
            const availabilityResponse = await API_HELPER.apiCall(availabilityUrl, availabilityParams, { method: 'GET' });
            if (availabilityResponse && availabilityResponse.data && availabilityResponse.data.length > 0) {
              const availability = availabilityResponse.data.find(av => 
                av.experienceId === this.reservation.experienceId && 
                av.availableDate === this.reservation.reservationDate
              );
              if (availability) {
                this.reservation.experienceAvailability = availability;
              }
            }
          } catch (availabilityError) {
            // Availability data not critical, continue loading
          }
        }
        
        // People data is already included in the reservation response from the API
        // No need to make additional API calls
        
        // Load experience data if not present
        if (!this.reservation.experience && this.reservation.experienceId) {
          const experienceUrl = API_HELPER.pathJoin([process.env.VUE_APP_API_ROOT, 'Experiences', this.reservation.experienceId]);
          const experienceResponse = await API_HELPER.apiCall(experienceUrl, null, { method: 'GET' });
          if (experienceResponse && experienceResponse.data) {
            this.reservation.experience = experienceResponse.data;
          }
        }
        
      } catch (error) {
        console.error('Error loading additional data:', error);
      }
    },
    
    async updateAdminNotes() {
      if (!this.reservation || !this.reservation.id) {
        this.$store.dispatch('alert/setAlert', {
          type: 'error',
          message: 'Reservation data not available'
        });
        return;
      }
      
      try {
        // The PUT contract replaces the whole reservation, so send the complete payload
        const people = Array.isArray(this.reservation.people) ? this.reservation.people : [];
        await this.update({
          id: this.reservation.id,
          experienceId: this.reservation.experienceId,
          experienceAvailabilityId: this.reservation.experienceAvailabilityId,
          touristName: this.reservation.touristName,
          touristEmail: this.reservation.touristEmail,
          touristPhone: this.reservation.touristPhone,
          reservationDate: this.reservation.reservationDate,
          numberOfPeople: this.reservation.totalPeople || people.length,
          language: this.reservation.language,
          restrictions: this.reservation.restrictions || '',
          notes: this.reservation.notes || '',
          needsPrePayment: this.reservation.prePaymentRequired,
          prePaymentAmount: this.reservation.prePaymentAmount || 0,
          paymentMethod: this.reservation.paymentMethod,
          status: this.reservation.status,
          adminNotes: this.reservation.adminNotes || '',
          discountAmount: this.reservation.discountAmount || 0,
          people: people.map(person => ({
            id: person.id,
            name: person.name,
            ageBracket: person.ageBracket
          }))
        });
        this.$store.dispatch('alert/setAlert', {
          type: 'success',
          message: this.$t('reservations.notesUpdated')
        });
      } catch (error) {
        console.error('Error updating notes:', error);
        this.$store.dispatch('alert/setAlert', {
          type: 'error',
          message: this.$t('reservations.updateError')
        });
      }
    },
    
    async confirmReservationHandler() {
      if (!this.reservation || !this.reservation.id) {
        this.$store.dispatch('alert/setAlert', {
          type: 'error',
          message: 'Reservation data not available'
        });
        return;
      }
      
      try {
        // Call the confirm endpoint which will update status and send email
        await this.confirmReservation(this.reservationId);
        
        this.showConfirmModal = false;
        
        this.$store.dispatch('alert/setAlert', {
          type: 'success',
          message: this.$t('reservations.confirmedSuccess')
        });
        
        // Redirect to reservations list
        this.$router.push({ name: 'ReservationsList' });
      } catch (error) {
        console.error('Error confirming reservation:', error);
        this.$store.dispatch('alert/setAlert', {
          type: 'error',
          message: this.$t('reservations.confirmError')
        });
      }
    },
    
    cancelConfirmation() {
      this.showConfirmModal = false;
    },
    
    cancelCancelModal() {
      this.showCancelModal = false;
    },
    
    async cancelReservationHandler() {
      if (!this.reservation || !this.reservation.id) {
        this.$store.dispatch('alert/setAlert', {
          type: 'error',
          message: 'Reservation data not available'
        });
        return;
      }
      
      try {
        // Call the cancel endpoint which will update status and send email
        await this.cancelReservation(this.reservationId);
        
        this.showCancelModal = false;
        
        this.$store.dispatch('alert/setAlert', {
          type: 'success',
          message: this.$t('reservations.cancelledSuccess')
        });
        
        // Reload the reservation to reflect the changes
        await this.loadReservation();
      } catch (error) {
        console.error('Error cancelling reservation:', error);
        this.$store.dispatch('alert/setAlert', {
          type: 'error',
          message: this.$t('reservations.cancelError')
        });
      }
    },
    
    getStatusClass(status) {
      if (!status) return 'secondary';
      switch (status) {
        case 'Pending': return 'warning';
        case 'Confirmed': return 'success';
        case 'Cancelled': return 'danger';
        case 'Completed': return 'secondary';
        default: return 'secondary';
      }
    },
    
    getStatusText(status) {
      if (!status) return 'N/A';
      try {
        return this.$t(`reservations.status.${status.toLowerCase()}`);
      } catch (error) {
        return status;
      }
    },
    
    getLanguageName(lang) {
      return lang === 'pt' ? 'Português' : 'English';
    },
    
    getPaymentMethodText(method) {
      switch (method) {
        case 'BankTransfer': return this.$t('reservations.paymentMethods.bankTransfer');
        case 'CreditCard': return this.$t('reservations.paymentMethods.creditCard');
        default: return method;
      }
    },
    
    getAgeBracketName(bracket) {
      if (!bracket) return 'N/A';
      try {
        return this.$t(`reservations.ageBrackets.${bracket.toLowerCase()}`);
      } catch (error) {
        return bracket;
      }
    },
    
    formatDate(date) {
      return new Date(date).toLocaleDateString(this.$i18n.locale);
    },

    formatDateTime(date) {
      if (!date) return '';
      return new Date(date).toLocaleString(this.$i18n.locale);
    },

    formatPrice(amount) {
      if (!amount) return '0.00';
      return parseFloat(amount).toFixed(2);
    },

    async saveClientNote(sendEmail = false) {
      if (!this.reservation || !this.reservation.id) {
        this.$store.dispatch('alert/setAlert', {
          type: 'error',
          message: 'Reservation data not available'
        });
        return;
      }

      try {
        this.clientNoteSaving = true;
        await this.updateClientNote({
          id: this.reservationId,
          clientNote: this.clientNote,
          sendEmail: sendEmail
        });

        this.showClientNoteModal = false;

        // Update the local reservation data
        if (this.reservation) {
          this.reservation.clientNote = this.clientNote;
          this.reservation.clientNoteLastUpdatedAt = new Date().toISOString();
        }

        this.$store.dispatch('alert/setAlert', {
          type: 'success',
          message: sendEmail
            ? this.$t('reservations.clientNoteSentSuccess')
            : this.$t('reservations.clientNoteSavedSuccess')
        });
      } catch (error) {
        console.error('Error updating client note:', error);
        this.$store.dispatch('alert/setAlert', {
          type: 'error',
          message: sendEmail
            ? this.$t('reservations.clientNoteSendError')
            : this.$t('reservations.clientNoteSaveError')
        });
      } finally {
        this.clientNoteSaving = false;
      }
    },

    goBack() {
      this.$router.go(-1);
    }
  }
}
</script>

<style scoped>
.reservation-status .badge {
  font-size: 0.9rem;
  padding: 0.5rem 1rem;
}

.reservation-details h5 {
  color: var(--primary-color);
  margin-bottom: 1rem;
  border-bottom: 1px solid #dee2e6;
  padding-bottom: 0.5rem;
}

.table td {
  border-top: none;
  padding: 0.5rem 0.75rem;
}

.table td:first-child {
  width: 40%;
}

.btn {
  margin-right: 0.5rem;
}

.btn:last-child {
  margin-right: 0;
}

/* Modal Styles */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1050;
}

.modal-content {
  background: white;
  border-radius: 8px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  max-width: 500px;
  width: 90%;
  max-height: 90vh;
  overflow-y: auto;
}

.modal-header {
  padding: 1rem 1.5rem;
  border-bottom: 1px solid #dee2e6;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.modal-title {
  margin: 0;
  font-size: 1.25rem;
  font-weight: 600;
}

.close {
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: #6c757d;
}

.close:hover {
  color: #000;
}

.modal-body {
  padding: 1.5rem;
}

.modal-footer {
  padding: 1rem 1.5rem;
  border-top: 1px solid #dee2e6;
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
}
</style>

